import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    GetPaperPlanMetadataInput,
    GetWeightsProfilesInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tab, { TabGroup } from '@amzn/meridian/tab';

import { GridApi } from 'ag-grid-community';
import { Switch, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ALL_SERVICES, Service } from 'src/constants';
import { VIEWER_ROLE_SET } from 'src/constants/role';
import { AppContext } from 'src/contexts/appContext';
import { IWeightsProfileRowData } from 'src/types/IWeightsProfile';

import { BaseAgGrid } from '../BaseAgGrid';
import { FilterInput } from '../FilterInput';
import { ProtectedRoute } from '../ProtectedRoute';
import './PlanWeights.scss';
import {
    PLAN_WEIGHTS_SORT_MODEL,
    PLAN_WEIGHTS_TABS,
    getColDefs,
    mapPaperPlanMetadataResponseToMap,
    mapWeightsProfilesResponseToRowData
} from './planWeightsUtils';

export const PlanWeights: React.FC = () => {
    const { path } = useRouteMatch();
    const { setTopAlertMsg } = useContext(AppContext);
    const [filterText, setFilterText] = useState('');
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [currentTab, setCurrentTab] = useState(PLAN_WEIGHTS_TABS[0]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [rowData, setRowData] = useState<IWeightsProfileRowData[]>([]);
    const [services, setServices] = useState<Service[]>([...ALL_SERVICES]);

    useEffect(() => {
        const paperPlanMetadataPromise = contractConfigurationClient
            .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({}))
            .toPromise();
        const weightsProfilesPromise = contractConfigurationClient
            .getWeightsProfiles(GetWeightsProfilesInput.clone({}))
            .toPromise();

        Promise.all([paperPlanMetadataPromise, weightsProfilesPromise])
            .then((response) => {
                const [metaDataResponse, weightsProfilesResponse] = response;

                const paperPlansMap = mapPaperPlanMetadataResponseToMap(metaDataResponse);
                setRowData(mapWeightsProfilesResponseToRowData(weightsProfilesResponse, paperPlansMap));
                setIsDataLoaded(true);
            })
            .catch((error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message));
    }, []);

    const tabbedRowData = useMemo(
        () => rowData.filter((row) => row.entityId === currentTab.entityId),
        [rowData, currentTab]
    );

    const serviceSelect = (
        <Select prefix={'Services'} value={services} onChange={setServices}>
            {ALL_SERVICES.map((service) => (
                <SelectOption key={service} label={service} value={service} />
            ))}
        </Select>
    );

    useEffect(() => agGridApi?.sizeColumnsToFit(), [services, agGridApi]);

    return (
        <div className="plan-weights-container">
            <Switch>
                <ProtectedRoute exact path={path} requiredRoleSet={VIEWER_ROLE_SET}>
                    <Column className="plan-weights-div" width="100%" height="100%" minHeight={480} spacing="300">
                        <TabGroup
                            value={currentTab.name}
                            onChange={(tabName) =>
                                setCurrentTab(PLAN_WEIGHTS_TABS.find((tab) => tab.name.toString() === tabName)!)
                            }
                            fill="line"
                        >
                            {PLAN_WEIGHTS_TABS.map((tab) => (
                                <Tab value={tab.name} key={tab.name} id={tab.name} data-testid={tab.name}>
                                    {tab.name}
                                </Tab>
                            ))}
                        </TabGroup>
                        <Box type="fill" spacingInset="400">
                            <Row>
                                <FilterInput filterText={filterText} setFilterText={setFilterText} />
                                {serviceSelect}
                            </Row>
                        </Box>
                        <div className="plan-weights-grid">
                            <BaseAgGrid
                                columnDefs={getColDefs(currentTab.name, services)}
                                rowData={tabbedRowData}
                                filterText={filterText}
                                sortModel={PLAN_WEIGHTS_SORT_MODEL}
                                setAgGridApi={setAgGridApi}
                                loading={!isDataLoaded}
                            />
                        </div>
                    </Column>
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
